import React, { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { DateType, momTitleFunc } from '../../utils';
import { V2HighEnergyDeviceV2EnergyMonitoringPostResponse } from '@maxtropy/device-customer-apis-v2';
import { keepPercentOrLine, keepTwoNull, keepZeroPercentOrLine } from '@/utils/utils';
import { isNil } from 'lodash-es';
interface EnergyCardsProps {
  datas: V2HighEnergyDeviceV2EnergyMonitoringPostResponse;
  energyMediumId?: string;
  timeResolution?: string;
}
interface ElectricData {
  use?: number;
  useMom?: number;
  useYoy?: number;
  useRatio?: number;
  useCost?: number;
}
interface ElectricDataItem {
  title: string;
  data?: ElectricData;
  key: string;
  timeData?: ElectricData;
}
const ELECTRICID = '2030';
const EnergyStats: React.FC<EnergyCardsProps> = ({ timeResolution, datas, energyMediumId }) => {
  const [isDisplay, setIsDisplay] = useState<boolean>(energyMediumId !== ELECTRICID);
  const [isGranularity, setIsGranularity] = useState<boolean>(false);

  useEffect(() => {
    setIsDisplay(energyMediumId !== ELECTRICID);
  }, [energyMediumId]);

  useEffect(() => {
    if (timeResolution === DateType.WEEK || timeResolution === DateType.MONTH) {
      setIsGranularity(true);
    } else {
      setIsGranularity(false);
    }
  }, [timeResolution]);

  const electricDataList: ElectricDataItem[] = [
    {
      title: '尖时段用能',
      data: datas?.totalData?.summitElectricData,
      key: 'summit',
      timeData: datas?.periodData?.summitElectricData,
    },
    {
      title: '峰时段用能',
      data: datas?.totalData?.peakElectricData,
      key: 'peak',
      timeData: datas?.periodData?.peakElectricData,
    },
    {
      title: '平时段用能',
      data: datas?.totalData?.plainElectricData,
      key: 'plain',
      timeData: datas?.periodData?.plainElectricData,
    },

    {
      title: '谷时段用能',
      data: datas?.totalData?.valleyElectricData,
      key: 'valley',
      timeData: datas?.periodData?.valleyElectricData,
    },
  ];

  function renderTrendTheSame(periodDeal?: number) {
    if (isGranularity) {
      return (
        <div className={styles.same}>
          <span className={styles.compareText}>同比去年：</span>
          {TrendIndicator(periodDeal)}
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={styles.contain}
      style={{
        ...(isDisplay && { justifyContent: 'flex-start' }),
      }}
    >
      <div
        className={styles.card}
        style={{
          ...(isDisplay && { flex: '0 0 24%' }),
        }}
      >
        <div
          style={{
            ...(datas?.dataQueryPeriodId && {
              borderBottom: '1px dashed var(--mx-tabs-base-border-color)',
              paddingBottom: 8,
            }),
          }}
        >
          <div className={styles.title}>总用能({datas?.generalUnitName})</div>
          <div className={styles.value}>{keepTwoNull(datas?.totalData?.totalUse)}</div>
          {renderTrendTheSame(datas?.totalData?.totalUseYoy)}
          <div className={styles.compare}>
            <span className={styles.compareText}>{momTitleFunc(timeResolution as DateType)}：</span>
            {TrendIndicator(datas?.totalData?.totalUseMom)}
          </div>
          <div className={styles.compare}>
            <span className={styles.compareText}>用能成本：</span>
            <span className={styles.energy}> {keepTwoNull(datas.totalData?.totalUseCost)}元</span>
          </div>
        </div>

        {datas?.dataQueryPeriodId && (
          <div>
            <div className={styles.title} style={{ marginTop: 8 }}>
              {datas?.dataQueryPeriodName}总用能({datas?.generalUnitName})
            </div>
            <div className={styles.value}>{keepTwoNull(datas?.periodData?.totalUse)}</div>
            {renderTrendTheSame(datas?.periodData?.totalUseYoy)}
            <div className={styles.compare}>
              <span className={styles.compareText}>{momTitleFunc(timeResolution as DateType)}：</span>
              {TrendIndicator(datas?.periodData?.totalUseMom)}
            </div>
            <div className={styles.compare}>
              <span className={styles.compareText}>用能成本：</span>
              <span className={styles.energy}>{keepTwoNull(datas?.periodData?.totalUseCost)}元</span>
            </div>
          </div>
        )}
      </div>

      {isDisplay ? (
        <div className={styles.cardMaxMin}>
          <div className={styles.title}>
            最大值({datas?.generalUnitName})：
            <span className={styles.value}>{keepTwoNull(datas?.totalData?.max)}</span>
          </div>
          <div className={styles.title} style={{ marginBottom: 5 }}>
            最小值({datas?.generalUnitName})：
            <span className={styles.value}>{keepTwoNull(datas?.totalData?.min)}</span>
          </div>
        </div>
      ) : (
        electricDataList.map((item: ElectricDataItem) => {
          const { title, data: electricData, key, timeData: periodTimeData } = item;
          const { use, useMom, useYoy, useRatio, useCost } = electricData || {};
          const {
            use: periodUse,
            useMom: periodUseMom,
            useYoy: periodUseYoy,
            useRatio: periodUseRatio,
            useCost: periodUseCost,
          } = periodTimeData || {};

          return (
            <div key={key} className={styles.card}>
              <div
                style={{
                  ...(datas?.dataQueryPeriodId && {
                    borderBottom: '1px dashed var(--mx-tabs-base-border-color)',
                    paddingBottom: 8,
                  }),
                }}
              >
                <div className={styles.title}>
                  {title}({datas?.generalUnitName})
                </div>
                <div className={styles.value}>
                  {keepTwoNull(use)}
                  <span className={styles.proportion}>{useRatio ? `(${(useRatio * 100).toFixed(1)}%)` : '(--)'}</span>
                </div>
                {renderTrendTheSame(useYoy)}
                <div className={styles.compare}>
                  <span className={styles.compareText}>{momTitleFunc(timeResolution as DateType)}：</span>
                  {TrendIndicator(useMom)}
                </div>
                <div className={styles.compare}>
                  <span className={styles.compareText}>用能成本：</span>
                  <span className={styles.energy}>{keepTwoNull(useCost)}元</span>
                </div>
              </div>

              {datas?.dataQueryPeriodId && (
                <div>
                  <div className={styles.title} style={{ marginTop: 8 }}>
                    {datas?.dataQueryPeriodName}-{title}({datas?.generalUnitName})
                  </div>
                  <div className={styles.value}>
                    {keepTwoNull(periodUse)}
                    <span className={styles.proportion}>
                      {periodUseRatio ? `(${(periodUseRatio * 100).toFixed(1)}%)` : '(--)'}
                    </span>
                  </div>
                  {renderTrendTheSame(periodUseYoy)}
                  <div className={styles.compare}>
                    <span className={styles.compareText}>{momTitleFunc(timeResolution as DateType)}：</span>
                    {TrendIndicator(periodUseMom)}
                  </div>
                  <div className={styles.compare}>
                    <span className={styles.compareText}>用能成本：</span>
                    <span className={styles.energy}>{keepTwoNull(periodUseCost)}元</span>
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default EnergyStats;

const TrendIndicator = (value: number | undefined) => {
  if (isNil(value)) return `--`;
  const currentValue = value ?? 0;
  const isUp = value > 0;
  const displayValue = isUp ? currentValue : Math.abs(currentValue);

  return (
    <span className={isUp ? styles.up : styles.down}>
      {keepPercentOrLine(displayValue)}% {isUp ? '↑' : '↓'}
    </span>
  );
};
